(function () {
  'use strict';

  if (window.UnifiedContext) {
    return;
  }

  const VERSION = '1.0.0';

  const STORAGE_KEYS = {
    sessionId: 'ut_session_id',
    pageSequence: 'ut_page_sequence'
  };

  const pageLoadedAtTimestamp = Date.now();

  const performanceStartedAt =
    window.performance &&
    typeof window.performance.now === 'function'
      ? window.performance.now()
      : 0;

  let maximumScrollDepth = 0;

  function safeSessionStorageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeSessionStorageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);

      return true;
    } catch (error) {
      return false;
    }
  }

  function safeSessionStorageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);

      return true;
    } catch (error) {
      return false;
    }
  }

  function generateFallbackId() {
    const randomPart = Math.random()
      .toString(36)
      .slice(2, 12);

    const timePart = Date.now()
      .toString(36);

    return `ut-${timePart}-${randomPart}`;
  }

  function generateSessionId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === 'function'
    ) {
      return `ut-${window.crypto.randomUUID()}`;
    }

    return generateFallbackId();
  }

  function initializeSessionId() {
    const existingSessionId = safeSessionStorageGet(
      STORAGE_KEYS.sessionId
    );

    if (existingSessionId) {
      return existingSessionId;
    }

    const newSessionId = generateSessionId();

    safeSessionStorageSet(
      STORAGE_KEYS.sessionId,
      newSessionId
    );

    return newSessionId;
  }

  function initializePageSequence() {
    const storedValue = safeSessionStorageGet(
      STORAGE_KEYS.pageSequence
    );

    const previousSequence = Number.parseInt(
      storedValue || '0',
      10
    );

    const validPreviousSequence =
      Number.isFinite(previousSequence) &&
      previousSequence >= 0
        ? previousSequence
        : 0;

    const currentSequence =
      validPreviousSequence + 1;

    safeSessionStorageSet(
      STORAGE_KEYS.pageSequence,
      String(currentSequence)
    );

    return currentSequence;
  }

  const sessionId = initializeSessionId();
  const pageSequence = initializePageSequence();

  function getDocumentHeight() {
    const body = document.body;
    const html = document.documentElement;

    return Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      html ? html.clientHeight : 0,
      html ? html.scrollHeight : 0,
      html ? html.offsetHeight : 0
    );
  }

  function getViewportHeight() {
    return (
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0
    );
  }

  function getCurrentScrollDepth() {
    const documentHeight = getDocumentHeight();
    const viewportHeight = getViewportHeight();

    const scrollableHeight =
      documentHeight - viewportHeight;

    if (scrollableHeight <= 0) {
      return 100;
    }

    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      0;

    const depth =
      (scrollPosition / scrollableHeight) * 100;

    return Math.max(
      0,
      Math.min(100, depth)
    );
  }

  function updateMaximumScrollDepth() {
    const currentDepth = getCurrentScrollDepth();

    if (currentDepth > maximumScrollDepth) {
      maximumScrollDepth = currentDepth;
    }
  }

  function getMaximumScrollDepth() {
    updateMaximumScrollDepth();

    return Number(
      maximumScrollDepth.toFixed(2)
    );
  }

  function getTimeSincePageLoad() {
    if (
      window.performance &&
      typeof window.performance.now === 'function'
    ) {
      return Math.max(
        0,
        Math.round(
          window.performance.now() -
          performanceStartedAt
        )
      );
    }

    return Math.max(
      0,
      Date.now() - pageLoadedAtTimestamp
    );
  }

  function getSecondsSincePageLoad() {
    return Number(
      (
        getTimeSincePageLoad() / 1000
      ).toFixed(2)
    );
  }

  function getReferrerDomain() {
    if (!document.referrer) {
      return '';
    }

    try {
      return new URL(
        document.referrer
      ).hostname;
    } catch (error) {
      return '';
    }
  }

  function getNavigationType() {
    if (
      !window.performance ||
      typeof window.performance.getEntriesByType !==
        'function'
    ) {
      return '';
    }

    const navigationEntries =
      window.performance.getEntriesByType(
        'navigation'
      );

    if (
      !navigationEntries ||
      !navigationEntries.length
    ) {
      return '';
    }

    return navigationEntries[0].type || '';
  }

  function getContext() {
    updateMaximumScrollDepth();

    return {
      ut_session_id: sessionId,

      page_sequence: pageSequence,

      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      page_language:
        document.documentElement.lang || '',

      page_loaded_at: new Date(
        pageLoadedAtTimestamp
      ).toISOString(),

      time_since_page_load:
        getTimeSincePageLoad(),

      seconds_since_page_load:
        getSecondsSincePageLoad(),

      scroll_depth:
        getMaximumScrollDepth(),

      referrer_domain:
        getReferrerDomain(),

      navigation_type:
        getNavigationType()
    };
  }

  function resetSession() {
    safeSessionStorageRemove(
      STORAGE_KEYS.sessionId
    );

    safeSessionStorageRemove(
      STORAGE_KEYS.pageSequence
    );

    return true;
  }

  window.addEventListener(
    'scroll',
    updateMaximumScrollDepth,
    {
      passive: true
    }
  );

  window.addEventListener(
    'resize',
    updateMaximumScrollDepth,
    {
      passive: true
    }
  );

  updateMaximumScrollDepth();

  window.UnifiedContext = {
    version: VERSION,

    getContext: getContext,

    getSessionId: function () {
      return sessionId;
    },

    getPageSequence: function () {
      return pageSequence;
    },

    getScrollDepth:
      getMaximumScrollDepth,

    getTimeSincePageLoad:
      getTimeSincePageLoad,

    resetSession: resetSession
  };

  if (
    window.UnifiedTrackingConfig &&
    window.UnifiedTrackingConfig.debug
  ) {
    console.info(
      '[Unified Context] Context initialized',
      {
        version: VERSION,
        sessionId: sessionId,
        pageSequence: pageSequence
      }
    );
  }
})();
